const STATUS_LABELS = {
  pending_approval: 'Awaiting approval',
  awaiting_finance: 'Awaiting finance',
  finance_approved: 'Finance approved',
  escrow_locked: 'Escrow locked',
  paid: 'Escrow locked',
  transport_sourced: 'Transport sourced',
  pending_dispatch: 'Pending dispatch',
  pickup_completed: 'Pickup completed',
  in_transit: 'In transit',
  delivered: 'Delivered',
  released: 'Delivered and settled',
  rejected: 'Rejected',
};

const TERMINAL_STATUSES = new Set(['delivered', 'released', 'rejected', 'cancelled', 'completed']);

const ORDER_FALLBACK_STEPS = [
  'awaiting_finance',
  'escrow_locked',
  'transport_sourced',
  'pending_dispatch',
  'pickup_completed',
  'in_transit',
  'delivered',
];

const TRANSPORT_FALLBACK_STEPS = [
  'transport_sourced',
  'pending_dispatch',
  'pickup_completed',
  'in_transit',
  'delivered',
];

function humanize(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function extractStatusFromNotes(notes) {
  const match = String(notes || '').match(/updated to:\s*([a-z_]+)/i);
  return match?.[1] || '';
}

function pickFirst(...values) {
  return values.find(value => value !== undefined && value !== null && value !== '');
}

function getStatus(item) {
  return pickFirst(
    item?.status,
    item?.action,
    item?.fulfillment_status,
    item?.escrow_status,
    item?.step,
    item?.event,
    item?.label
  );
}

function getTimestamp(item) {
  return pickFirst(
    item?.timestamp,
    item?.event_time,
    item?.created_at,
    item?.date_created,
    item?.date_updated
  );
}

function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}, ${date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`;
}

function createTrailItem({ key, label, meta, state }) {
  return { key, label, meta, state };
}

export function normalizeOrderTrail(items = []) {
  const trail = [...items]
    .sort((a, b) => {
      const aTime = new Date(getTimestamp(a) || 0).getTime();
      const bTime = new Date(getTimestamp(b) || 0).getTime();
      return aTime - bTime;
    })
    .map((item, index, arr) => {
      const status = getStatus(item);
      const targetStatus = pickFirst(
        item?.target_status,
        extractStatusFromNotes(item?.notes),
        extractStatusFromNotes(item?.note),
        item?.fulfillment_status,
        item?.escrow_status
      );
      const label = pickFirst(
        item?.label,
        item?.title,
        STATUS_LABELS[targetStatus],
        humanize(targetStatus),
        STATUS_LABELS[status],
        humanize(status),
        'Update'
      );
      const timestamp = formatTimestamp(getTimestamp(item));
      const isLast = index === arr.length - 1;
      const state = isLast && !TERMINAL_STATUSES.has(status) ? 'active' : 'done';
      const stateLabel = state === 'done' ? 'Completed' : 'In progress';
      const meta = timestamp ? `${stateLabel} • ${timestamp}` : stateLabel;

      return createTrailItem({
        key: item?.id || `${status || 'trail'}-${index}`,
        label,
        meta,
        state,
      });
    });

  return trail;
}

export function buildFallbackTrail(currentStatus, createdAt, mode = 'order') {
  const steps = mode === 'transport' ? TRANSPORT_FALLBACK_STEPS : ORDER_FALLBACK_STEPS;
  const activeIndex = Math.max(steps.indexOf(currentStatus), 0);
  const createdLabel = formatTimestamp(createdAt);

  return steps.map((status, index) => {
    const state = index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending';
    const stateLabel = state === 'done' ? 'Completed' : 'In progress';
    return createTrailItem({
      key: status,
      label: STATUS_LABELS[status] || humanize(status),
      meta: state === 'pending' ? '' : createdLabel ? `${stateLabel} • ${createdLabel}` : stateLabel,
      state,
    });
  });
}
